// Adapter between the backend BoardDetail/Row/Cell model and the flat
// design view-model the UI components were prototyped against.
//
// The design's `row` shape is: { id, title, status, owners, due, budget, tags,
// notes, link, done } — each backed by a typed column.  We project the real
// row.cells into that shape using column name+type for matching.
//
// Why match by name: the v1 importer + first-board seed both produce columns
// named exactly like the German labels in the design.  If a column doesn't
// exist on a particular board, the corresponding row field is null/empty.

import type {
  BoardDetail,
  BoardKind,
  Cell,
  CellValue,
  ColumnConfig,
  ColumnDef,
  Row,
  StatusRole,
  UUID,
  ViewConfig,
} from "@/lib/types";
import type { StatusColor } from "@/lib/tokens";
import {
  sampleBoard,
  sampleLiveViewers,
  sampleMe,
  sampleUserById,
  sampleUsers,
  type SampleBoard,
  type SampleStatusOption,
  type SampleUser,
} from "@/lib/sample-data";

export interface ViewStatusOption {
  id: string;
  label: string;
  color: StatusColor;
  role?: StatusRole;
  /** Vorlaufzeit: per-section goal date when this column is the section column. */
  goal_date?: string;
  /** Archived: hidden from pickers/form but still resolved for display. */
  archived?: boolean;
}

export interface ViewColumn {
  id: string;
  type: ColumnDef["type"];
  name: string;
  width: number;
  options?: ViewStatusOption[];
  format?: string;
  currency?: string;
  raw?: ColumnDef;
}

export interface TimelineRange {
  start: string;
  end: string;
}

export interface ViewRow {
  id: string;
  title: string;
  status: string | null;
  owners: string[];
  due: string | null;
  /** Which date column `due` was read from (the board's FIRST date column). A board
   * can have several date columns ("Deadline", "Erledigt am"); only this one carries
   * the overdue/today deadline semantics in the table. */
  dueColumnId?: string | null;
  timeline: TimelineRange | null;
  budget: number | null;
  tags: string[];
  notes: string;
  link: { url: string; label: string } | null;
  done: boolean;
  /** Sub-items: id of the parent row, null = top-level. */
  parentRowId: string | null;
  /** Denormalised tree depth — 0 for top-level rows. */
  depth: number;
  /** Direct-child count, derived during adapt. Drives the chevron + badge. */
  childCount: number;
  /** Number of comments on the row — kanban card badge. */
  commentCount: number;
  /** ISO timestamp of the last status-cell change — kanban SLA timer. */
  statusChangedAt: string | null;
  /** Ticketing: per-board human number (-> "<board.ticketPrefix>-<n>"). */
  ticketNumber: number | null;
  /** ISO creation timestamp — ticket Timeline anchor (open-since) + age. */
  createdAt: string | null;
  /** ISO timestamp the ticket reached a closed role — ends the lifetime span. */
  resolvedAt: string | null;
  raw?: Row;
}

export interface ViewBoard {
  id: string;
  workspace_id: string;
  name: string;
  description: string;
  kind: BoardKind;
  columns: ViewColumn[];
  rows: ViewRow[];
  /** Shared per-board default view (visible columns, grouping, view type). */
  viewConfig: ViewConfig;
  /** Vorlaufzeit goal/target date (board.goal_date), ISO date or null. */
  goalDate: string | null;
  /** How the current user reaches this board: "member", "shared", or "guest". */
  access: "member" | "shared" | "guest";
  /** Ticketing: per-board ID prefix (e.g. "IT"), null on project boards. */
  ticketPrefix: string | null;
  /**
   * True when a meeting series points at this board (its Aufgaben board) — derived
   * server-side, never stored. This is the object RowDetailPanel actually receives,
   * so the Unteraufgaben section reads it from HERE, not from the raw Board. Kept
   * snake_case to match the wire field it mirrors 1:1. Required (not optional) on
   * purpose: every ViewBoard construction site must state what the board IS, and
   * typecheck says so.
   */
  is_aufgaben: boolean;
  /**
   * May the current user write to this board (add rows, edit cells)? Mirrors
   * `BoardDetail.can_write`, which only the board-DETAIL endpoint sets — absent (list
   * contexts, fixtures) means "treat as writable", so this defaults to true. The
   * Unteraufgaben section is the first consumer: a viewer/guest sees the sub-tasks but
   * gets no checkbox, no picker and no create box.
   */
  canWrite: boolean;
  /**
   * Is the current user an owner/admin of this board's workspace (or flow-admin)?
   * Mirrors `BoardDetail.can_admin`; absent (list contexts, fixtures) means "not
   * admin", so this defaults to false. Used to keep a ticket's Beschreibung
   * editable for admins while it is read-only for ordinary staff.
   */
  canAdmin: boolean;
}

/** True for ticket boards — the single switch for ticketing-mode UI. */
export function isTicketBoard(b: { kind: BoardKind }): boolean {
  return b.kind === "tickets";
}

/** Boards running the workflow-action lifecycle (tickets + ideas): status is
 * action-only, Beschreibung is edit-locked, the action bar shows. */
export function isWorkflowKind(kind: BoardKind): boolean {
  return kind === "tickets" || kind === "ideas";
}

export function isWorkflowBoard(b: { kind: BoardKind }): boolean {
  return isWorkflowKind(b.kind);
}

/** "<PREFIX>-<number>" (e.g. IT-42), or null when either part is missing. */
export function ticketLabel(prefix: string | null, num: number | null): string | null {
  return prefix && num != null ? `${prefix}-${num}` : null;
}

const FIELD_BY_TYPE: Record<string, keyof ViewRow> = {
  text: "title",
  long_text: "notes",
  status: "status",
  person: "owners",
  date: "due",
  timeline: "timeline",
  number: "budget",
  tags: "tags",
  link: "link",
  checkbox: "done",
};

const STATUS_COLOR_FALLBACK: StatusColor = "slate";

function normalizeStatusColor(input: unknown): StatusColor {
  if (typeof input !== "string") return STATUS_COLOR_FALLBACK;
  // Accept either palette keys or hex; hex falls back to slate (the rest of the
  // pipeline relies on palette keys for tint resolution).
  const KEYS: StatusColor[] = [
    "slate",
    "blue",
    "red",
    "amber",
    "green",
    "gray",
    "purple",
    "pink",
    "teal",
    "yellow",
  ];
  return (KEYS as string[]).includes(input) ? (input as StatusColor) : STATUS_COLOR_FALLBACK;
}

function readStatusOptions(config: ColumnConfig): ViewStatusOption[] | undefined {
  const opts = config.options;
  if (!Array.isArray(opts)) return undefined;
  return opts.map((o) => ({
    id: String(o.id ?? ""),
    label: String(o.label ?? ""),
    color: normalizeStatusColor(o.color),
    role: o.role,
    goal_date: typeof o.goal_date === "string" ? o.goal_date : undefined,
    archived: o.archived === true ? true : undefined,
  }));
}

// Title column auto-fit bounds. SOTA here is fit-to-content with a MAX width +
// ellipsis beyond (what Notion/Airtable do) — NOT a character limit on the value
// itself (limiting what someone can type is hostile; truncation is display-only).
const TITLE_MIN_W = 240;
const TITLE_MAX_W = 560;

// Width that fits the longest title value, capped. ~7.3px/char at the 13.5px
// medium title font + ~56px cell chrome (padding, gutter, chips).
function autoTitleWidth(rows: BoardDetail["rows"], columnId: string): number {
  let maxLen = 0;
  for (const r of rows) {
    const v = (r.cells ?? []).find((c) => c.column_def_id === columnId)?.value;
    const len = typeof v === "string" ? v.length : 0;
    if (len > maxLen) maxLen = len;
  }
  return Math.max(TITLE_MIN_W, Math.min(TITLE_MAX_W, Math.round(maxLen * 7.3 + 56)));
}

export function adaptBoard(detail: BoardDetail): ViewBoard {
  // The first (leftmost) text column is the row title/Name — auto-fit it to the
  // longest value (capped) so names aren't clipped and nobody resizes it every
  // time. Explicit/resized widths (config.width) always win.
  let titleWidthAssigned = false;
  const columns: ViewColumn[] = [...detail.columns]
    .sort((a, b) => a.position - b.position)
    .map((c) => {
      const config = c.config ?? {};
      let width: number;
      if (typeof config.width === "number") {
        width = config.width as number;
      } else if (c.type === "text" && !titleWidthAssigned) {
        width = autoTitleWidth(detail.rows, c.id);
        titleWidthAssigned = true;
      } else {
        width = 160;
      }
      const format = typeof config.format === "string" ? (config.format as string) : undefined;
      const currency =
        typeof config.currency === "string" ? (config.currency as string) : undefined;
      return {
        id: c.id,
        type: c.type,
        name: c.name,
        width,
        options: readStatusOptions(config),
        format,
        currency,
        raw: c,
      };
    });

  const rows: ViewRow[] = [...detail.rows]
    .sort((a, b) => a.position - b.position)
    .map((r) => projectRow(r, columns));

  // Compute childCount once over the full row list. v1 depth ≤ 1 so this is
  // a single pass; if we lift the cap we'd want a memoised lookup.
  const childCounts = new Map<string, number>();
  for (const r of rows) {
    if (!r.parentRowId) continue;
    childCounts.set(r.parentRowId, (childCounts.get(r.parentRowId) ?? 0) + 1);
  }
  for (const r of rows) {
    r.childCount = childCounts.get(r.id) ?? 0;
  }

  return {
    id: detail.id,
    workspace_id: detail.workspace_id,
    name: detail.name,
    description: detail.description ?? "",
    kind: detail.kind,
    columns,
    rows,
    viewConfig: detail.view_config ?? {},
    goalDate: detail.goal_date ?? null,
    access: detail.access ?? "member",
    ticketPrefix: detail.ticket_prefix ?? null,
    is_aufgaben: detail.is_aufgaben ?? false,
    canWrite: detail.can_write ?? true,
    canAdmin: detail.can_admin ?? false,
  };
}

/** A section's year for the year-tabs filter: from its Zieltermin (goal_date)
 * if set, else a 4-digit 20xx parsed from the section label ("…1/2026"). */
export function deriveSectionYear(label: string, goalDate?: string | null): number | null {
  if (goalDate && /^\d{4}/.test(goalDate)) {
    const y = Number.parseInt(goalDate.slice(0, 4), 10);
    if (!Number.isNaN(y)) return y;
  }
  const m = label.match(/(20\d{2})/);
  return m && m[1] ? Number.parseInt(m[1], 10) : null;
}

/** Resolve which year tab a board opens on (sticky-tab, per-user, per-board).
 *
 * `saved` is the raw localStorage value for this board (`"all"`, a year string, or null);
 * `availableYears` are the years the board currently offers; `currentYear` is today's year
 * (injected so this stays pure and testable). Priority:
 *   1. `"all"` → Alle (null) — the user explicitly pinned it.
 *   2. a saved year that is STILL available → that year.
 *   3. otherwise (no saved value, or a stale/garbage one) → the default: the current year if
 *      the board offers it, else Alle. A stale saved year can never strand the user on an
 *      empty filter — it falls through to the default. */
export function resolveInitialYearTab(
  saved: string | null,
  availableYears: number[],
  currentYear: number,
): number | null {
  if (saved === "all") return null;
  if (saved != null) {
    const y = Number.parseInt(saved, 10);
    if (!Number.isNaN(y) && availableYears.includes(y)) return y;
    // stale/garbage saved value → fall through to the default below
  }
  return availableYears.includes(currentYear) ? currentYear : null;
}

function findFirstColumnId(columns: ViewColumn[], type: ColumnDef["type"]): string | undefined {
  return columns.find((c) => c.type === type)?.id;
}

function readCell(cells: Cell[], columnId: string | undefined): CellValue {
  if (!columnId) return null;
  return cells.find((c) => c.column_def_id === columnId)?.value ?? null;
}

function projectRow(row: Row, columns: ViewColumn[]): ViewRow {
  const titleId = findFirstColumnId(columns, "text");
  const statusId = findFirstColumnId(columns, "status");
  const personId = findFirstColumnId(columns, "person");
  const dueId = findFirstColumnId(columns, "date");
  const timelineId = findFirstColumnId(columns, "timeline");
  const budgetId = findFirstColumnId(columns, "number");
  const tagsId = findFirstColumnId(columns, "tags");
  const notesId = findFirstColumnId(columns, "long_text");
  const linkId = findFirstColumnId(columns, "link");
  const doneId = findFirstColumnId(columns, "checkbox");

  // An optimistic/WS-echoed row can arrive before its cells are populated, so
  // never assume row.cells is present — a missing array must degrade to an
  // empty row, not throw and white-screen the whole board.
  const cells = row.cells ?? [];
  const titleVal = readCell(cells, titleId);
  const statusVal = readCell(cells, statusId);
  // Status cell's own updated_at is the closest thing to "status changed at"
  // without an event-log query. Falls back to row creation time.
  const statusCell = statusId ? cells.find((c) => c.column_def_id === statusId) : undefined;
  const personVal = readCell(cells, personId);
  const dueVal = readCell(cells, dueId);
  const timelineVal = readCell(cells, timelineId);
  const budgetVal = readCell(cells, budgetId);
  const tagsVal = readCell(cells, tagsId);
  const notesVal = readCell(cells, notesId);
  const linkVal = readCell(cells, linkId);
  const doneVal = readCell(cells, doneId);

  return {
    id: row.id,
    title: typeof titleVal === "string" ? titleVal : "",
    status: typeof statusVal === "string" ? statusVal : null,
    owners: Array.isArray(personVal)
      ? (personVal.filter((v) => typeof v === "string") as string[])
      : [],
    due: typeof dueVal === "string" ? dueVal.slice(0, 10) : null,
    dueColumnId: dueId ?? null,
    timeline:
      timelineVal &&
      typeof timelineVal === "object" &&
      !Array.isArray(timelineVal) &&
      // Require real string endpoints — a partial {start, end:null} must NOT be
      // String()-coerced to the literal "null" (would poison the timeline axis).
      typeof (timelineVal as Record<string, unknown>).start === "string" &&
      typeof (timelineVal as Record<string, unknown>).end === "string" &&
      (timelineVal as Record<string, unknown>).start &&
      (timelineVal as Record<string, unknown>).end
        ? {
            start: (timelineVal as unknown as { start: string }).start,
            end: (timelineVal as unknown as { end: string }).end,
          }
        : null,
    budget: typeof budgetVal === "number" ? budgetVal : null,
    tags: Array.isArray(tagsVal) ? (tagsVal.filter((v) => typeof v === "string") as string[]) : [],
    notes: typeof notesVal === "string" ? notesVal : "",
    link:
      linkVal && typeof linkVal === "object" && "url" in (linkVal as Record<string, unknown>)
        ? {
            url: String((linkVal as { url: unknown }).url ?? ""),
            label: String(
              (linkVal as { label?: unknown }).label ?? (linkVal as { url: unknown }).url ?? "",
            ),
          }
        : null,
    done: doneVal === true,
    parentRowId: row.parent_row_id ?? null,
    depth: row.depth ?? 0,
    childCount: 0,
    commentCount: row.comment_count ?? 0,
    statusChangedAt: statusCell?.updated_at ?? row.created_at ?? null,
    ticketNumber: row.ticket_number ?? null,
    createdAt: row.created_at ?? null,
    resolvedAt: row.resolved_at ?? null,
    raw: row,
  };
}

export function findColumnByField(board: ViewBoard, field: keyof ViewRow): ViewColumn | undefined {
  const type = (Object.entries(FIELD_BY_TYPE).find(([, f]) => f === field) ?? [])[0] as
    | ColumnDef["type"]
    | undefined;
  if (!type) return undefined;
  return board.columns.find((c) => c.type === type);
}

// Build the cell payload for a board field update. Returns the column def id +
// the JSONB-compatible value. Returns null if there is no matching column.
export function buildCellWrite(
  board: ViewBoard,
  field: keyof ViewRow,
  value: ViewRow[keyof ViewRow],
): { columnId: UUID; value: CellValue } | null {
  const col = findColumnByField(board, field);
  if (!col) return null;
  let next: CellValue;
  switch (col.type) {
    case "text":
    case "long_text":
      next = typeof value === "string" ? value : null;
      break;
    case "status":
      next = typeof value === "string" ? value : null;
      break;
    case "person":
    case "tags":
      next = Array.isArray(value) ? (value as string[]) : null;
      break;
    case "date":
      next = typeof value === "string" ? value : null;
      break;
    case "timeline":
      next =
        value && typeof value === "object" && "start" in (value as Record<string, unknown>)
          ? (value as { start: string; end: string })
          : null;
      break;
    case "number":
      next = typeof value === "number" ? value : null;
      break;
    case "checkbox":
      next = Boolean(value);
      break;
    case "link":
      next = value && typeof value === "object" ? (value as { url: string; label: string }) : null;
      break;
    default:
      next = null;
  }
  return { columnId: col.id, value: next };
}

// Convert the fixture sample to a ViewBoard.
export function fixtureBoard(): ViewBoard {
  return adaptSampleBoard(sampleBoard);
}

export function adaptSampleBoard(b: SampleBoard): ViewBoard {
  return {
    id: b.id,
    workspace_id: b.ws,
    name: b.name,
    description: b.description,
    kind: (b as { kind?: BoardKind }).kind ?? "project",
    columns: b.columns.map((c) => ({
      id: c.id,
      type: c.type as ColumnDef["type"],
      name: c.name,
      width: c.width,
      options: c.options?.map((o: SampleStatusOption) => ({
        id: o.id,
        label: o.label,
        color: o.color,
      })),
      ...(c.format ? { format: c.format } : {}),
      ...(c.currency ? { currency: c.currency } : {}),
    })),
    rows: b.rows.map((r) => ({
      id: r.id,
      title: r.title,
      status: r.status,
      owners: r.owners,
      due: r.due,
      timeline: null,
      budget: r.budget,
      tags: r.tags,
      notes: r.notes,
      link: r.link,
      done: r.done,
      parentRowId: null,
      depth: 0,
      childCount: 0,
      commentCount: 0,
      statusChangedAt: null,
      ticketNumber: null,
      createdAt: null,
      resolvedAt: null,
    })),
    viewConfig: {},
    goalDate: null,
    access: "member",
    ticketPrefix: null,
    // The offline demo fixture is a plain project board — no meeting series exists to
    // point at it, so it is not an Aufgaben board and shows no Unteraufgaben.
    is_aufgaben: false,
    canWrite: true,
    canAdmin: false,
  };
}

export interface UserDirectory {
  me: SampleUser;
  byId: (id: string) => SampleUser | undefined;
  all: SampleUser[];
  liveViewers: string[];
}

// Until the real user directory + presence endpoints exist, every consumer of
// "users" goes through this. Real backend wire-up TODO when /workspaces/:id/members ships.
export const userDirectory: UserDirectory = {
  me: sampleMe,
  byId: sampleUserById,
  all: sampleUsers,
  liveViewers: sampleLiveViewers,
};
